import * as React from 'preact/compat'
import Game from "./Game"
import Api from "../../api"
import Modal from "../Modal";
import Loading from "../Loading"
import { captureFetchException, loadStylesheet, showModal, throttle } from "../../common/helpers"
import formCss from '../../../css/form.pcss.json'
import styles from '../../../css/game-search-modal.pcss.json'
import buttonsCss from '../../../css/buttons.pcss.json'
import GameEditorModal from '../GameEditor/GameEditorModal'
import { GameEditorMode } from '../GameEditor/GameEditor'
import { showToast } from '../toast'

interface PropTypes {
  title: string,
  onClose: () => void,
  disableSuggest?: boolean,
  addGame: (gameId: number, gameInfo: {
    poster: string,
    status: number,
    url: string,
    name: string,
    releaseDate: string,
  }) => void,
  onRequest?: (requestInfo: {
    isRequest: boolean,
    id?: number,
    name: string,
    poster?: string
  }) => void,
  hidePushPrompt?: boolean,
}

interface StateTypes {
  search: string,
  loading: boolean,
  results: Array<{
    id: number,
    poster: string,
    status: number
    title: string,
    date: string,
    url: string
  }>,
  isFinal: boolean,
  total: number | string,
  selectedResult?: number,
}

class AddGameModal extends React.Component<PropTypes, StateTypes> {
  state: StateTypes = {
    search: '',
    loading: false,
    results: [],
    total: 0,
    isFinal: false,
  }

  modal = React.createRef<Modal>()
  input = React.createRef<HTMLInputElement>()

  componentWillMount() {
    // noinspection JSIgnoredPromiseFromCall
    loadStylesheet('game-search-modal')
    // noinspection JSIgnoredPromiseFromCall
    loadStylesheet('form')
    // noinspection JSIgnoredPromiseFromCall
    loadStylesheet('buttons')
  }

  componentDidMount() {
    // Хак, позволяющий обойти особенности работы React.Portal в Modal
    setTimeout(() => {
      if (this.input.current) {
        this.input.current.focus()
      }
    }, 1)
    document.addEventListener('keydown', this.handleKeyDown)
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown)
  }

  handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      e.stopPropagation()
      this.selectNext()
      return
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault()
      e.stopPropagation()
      this.selectPrevious()
      return
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      this.confirmSelection()
      return
    }
  }

  selectNext = () => {
    const { selectedResult, results } = this.state
    const next = selectedResult === undefined ? 0 : selectedResult + 1
    if (next < results.length) {
      this.setState({ selectedResult: next })
    }
  }

  selectPrevious = () => {
    const { selectedResult } = this.state
    const next = selectedResult === undefined ? 0 : selectedResult - 1
    if (next >= 0) {
      this.setState({ selectedResult: next })
    }
  }

  confirmSelection = () => {
    const { selectedResult, results } = this.state
    if (selectedResult !== undefined) {
      this.add(results[selectedResult])
    }
  }

  close = async () => {
    await this.modal.current.close()
  }

  suggestGame = async () => {
    await this.close()
    showModal(GameEditorModal, {
      game_id: 'new',
      mode: GameEditorMode.suggest_game,
      hidePushPrompt: this.props.hidePushPrompt,
      onSuccess: this.props.onRequest
    })
  }

  search = async (offset: number = 0) => {
    const { search } = this.state

    const state: Partial<StateTypes> = {
      loading: true
    }

    if (offset === 0) {
      state.results = []
      state.isFinal = false
    }

    this.setState(state)

    const searchParams = new URLSearchParams();
    searchParams.append('term', search)
    if (offset > 0) {
      searchParams.append('offset', `${offset}`)
    }

    Api.get<{
      results: Array<{
        id: number,
        square_poster: string,
        status: number,
        title: string,
        date: string,
        url: string,
        type: string
      }>,
      total: {
        games: number | string
      },
      isFinal: boolean
    }>({ url: `search/games/?${searchParams}` })
      .then(res => {
        const realResults = res.results.filter(game => game.type === 'game')
          .map(game => {
            return {
              id: game.id,
              poster: game.square_poster,
              status: game.status,
              title: game.title,
              date: game.date,
              url: game.url
            }
          })
          .filter(game => this.state.results.find(g => g.id === game.id) === undefined)

        const state: Partial<StateTypes> = {
          loading: false,
          isFinal: res.isFinal
        }

        if (offset > 0) {
          state.results = [...this.state.results, ...realResults]
        } else {
          state.results = realResults
          state.selectedResult = undefined
          state.total = res.total.games
        }
        this.setState(state)
      })
      .catch(err => {
        showToast({
          type: 'error',
          text: 'Во время поиска произошла ошибка'
        })
        captureFetchException(err)
        this.setState({
          loading: false,
          results: [],
          total: 0,
        })
      })
  }

  throttledSearch = throttle(this.search, 1000)

  onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ search: e.currentTarget.value }, () => {
      this.throttledSearch()
    })
  }

  add = (GameInfo: {
    id: number,
    poster: string,
    status: number,
    url: string
    title: string,
    date: string
  }) => {
    this.props.addGame(GameInfo.id, {
      poster: GameInfo.poster,
      status: GameInfo.status,
      url: GameInfo.url,
      name: GameInfo.title,
      releaseDate: GameInfo.date
    })
    // noinspection JSIgnoredPromiseFromCall
    this.close()
  }

  onScroll = (e: React.TargetedEvent<HTMLDivElement>) => {
    const { loading, results, isFinal, total } = this.state
    const bottomOffset = e.currentTarget.scrollHeight - e.currentTarget.scrollTop - e.currentTarget.clientHeight
    let totalItems = total
    if (typeof totalItems === 'string') {
      totalItems = parseInt(totalItems)
    }
    if (bottomOffset < 100 && !loading && results.length < totalItems && !isFinal) {
      // noinspection JSIgnoredPromiseFromCall
      this.search(results.length)
    }
  }

  render() {
    const { onClose, title, disableSuggest } = this.props
    const { search, loading, results, selectedResult } = this.state

    return <Modal ref={this.modal} onClose={onClose} showClose enableScroll mobileFullScreen>
      <div className={styles['container']}>
        <span className={styles['title']}>{title}</span>
        <input type='text' ref={this.input} value={search} onInput={this.onInput} className={formCss['input']} style={{
          width: '100%',
          marginBottom: '32px'
        }} autoFocus={true}/>
        <div className={styles['search-results']} onScroll={this.onScroll}>
          {results.length > 0 && <div style='display: flex; flex-direction: column;'>
            {results.map((item, key) => <Game
              key={item.id}
              game={item}
              add={this.add}
              selected={selectedResult === key}
            />)}
          </div>}
          {!loading && results.length === 0 &&
            <div style='display: flex; justify-content: center;'>
              {disableSuggest
                ? 'Ничего не найдено'
                : <button type='button' className={buttonsCss['outline']} onClick={this.suggestGame}>
                  Нет нужной игры?
                </button>
              }
            </div>
          }
          {loading && <Loading/>}
        </div>
      </div>
    </Modal>
  }
}

export default AddGameModal
